import { useState } from 'react'
import { Overlay } from '../../platform/overlay/Overlay'
import { Button } from '../../platform/primitives/Button'
import { ApplyFromField } from './ApplyFromField'
import { applyAllFailed, CONTINUE_FAILED, count } from './applyCopy'
import { localToday, longDate, windowStart } from './backfillWindow'
import { useApplyAllRules, type AppliedAll } from './useRuleApply'
import { RulesError } from './useRules'
import './rules.css'
import './rulePage.css'

/**
 * Apply all rules (plan 2026-10-15, Task 7): every enabled rule over a window of past transactions,
 * as one batch per run that the toast can undo.
 *
 * - **From** is always explicit and shown, as in Apply now; the default is 90 days ago.
 * - There is no dry run for apply-all, so instead of a count the dialog says what it will do. The
 *   server fills empty fields only and never writes one the user set.
 * - A run the server cut short is never reported as complete: the dialog stays up with "Changed at
 *   least N. Run it again to continue.", and **Run again** continues from the server's cursor
 *   (`resume_after`), over the same From. Never `resume_date_to` as `date_to`: resuming by date loops
 *   when one date holds more rows than the limit (backend 521).
 * - A cursor the server refuses (422) says "Couldn't continue. Start again from the beginning." and
 *   is dropped, so the next run starts the window afresh.
 * - Each run reports itself through `onRun` (the toast and its Undo) the moment it lands.
 *
 * `--accent` fills the run button only, the one primary action here.
 */
export function ApplyAllDialog({ onClose, onRun }: { onClose: () => void; onRun: (result: AppliedAll) => void }) {
  const today = localToday()
  const defaultFrom = windowStart(null, today)
  const [from, setFrom] = useState(defaultFrom)
  /** Set once a run is cut short: the next run continues from here, and From is fixed. */
  const [resumeAfter, setResumeAfter] = useState<string | null>(null)
  const [left, setLeft] = useState<number | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const apply = useApplyAllRules()
  const applying = apply.isPending
  const continuing = resumeAfter !== null

  const guard = (f: () => void) => () => {
    if (!applying) f()
  }

  const run = () => {
    if (applying) return
    setFailed(null)
    apply.mutate(
      { from, resumeAfter },
      {
        onSuccess: (result) => {
          onRun(result)
          if (result.atLeast && result.resumeAfter !== null) {
            setResumeAfter(result.resumeAfter)
            setLeft(result.modified)
          } else {
            onClose()
          }
        },
        onError: (e) => {
          const status = e instanceof RulesError ? e.status : undefined
          if (continuing && status === 422) {
            setResumeAfter(null)
            setLeft(null)
            setFailed(CONTINUE_FAILED)
          } else setFailed(applyAllFailed(status))
        },
      },
    )
  }

  const label = applying ? 'Applying…' : continuing ? 'Run again' : 'Apply'

  return (
    <Overlay open onClose={guard(onClose)} role="dialog" label="Apply all rules">
      <div className="rule-confirm">
        <h2 className="rule-confirm__title">Apply all rules</h2>
        {/* Fixed while continuing: the next run covers the rest of THIS window. */}
        <ApplyFromField value={from} onChange={setFrom} readOnly={continuing || applying} />
        <p className="rule-confirm__body">
          Fills empty fields on transactions since {longDate(from)}. Fields you set yourself are left alone.
        </p>
        {/* Mounted from the start, so a run cut short is announced. */}
        <p className="rule-confirm__status" role="status">
          {left !== null ? `Changed at least ${count(left)}. Run it again to continue.` : ''}
        </p>
        <p className="rule-confirm__facts">You can undo each run right after.</p>
        {failed !== null && (
          <p className="rule-confirm__error" role="alert">
            {failed}
          </p>
        )}
        <div className="rule-confirm__actions">
          <Button variant="outline" size="page" aria-disabled={applying || undefined} onClick={guard(onClose)}>
            {continuing ? 'Done' : 'Cancel'}
          </Button>
          <Button variant="primary" size="page" aria-disabled={applying || undefined} aria-busy={applying || undefined} onClick={run}>
            {label}
          </Button>
        </div>
      </div>
    </Overlay>
  )
}
